import { Stack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';

import { useProfileContext } from 'Contexts/profileContext';

import { ProviderPreferenceQuestion } from './ProviderPreferenceQuestion';
import Question from '../_shared/Question';
import {
  NetworkPreferenceSurveyAnswer,
  NetworkPreferenceSurveyAnswers,
  NetworkPreferenceSurveyQuestion,
} from '../types';

export interface ProviderPreferencesSectionProps {
  networkPreferenceSurvey: NetworkPreferenceSurveyQuestion[];
  networkPreferenceSurveyAnswers: NetworkPreferenceSurveyAnswers;
  isDirty: boolean;
  isProfileAudioEnabled?: boolean;
  submitNetworkPreferenceSurveyResponses: (responses: NetworkPreferenceSurveyAnswers) => void;
  handleSectionChange: (isValid: boolean, isComplete: boolean) => void;
}

// The parent hides this section entirely when there are no questions, so it only has
// to handle the case where at least one is asked.
export const ProviderPreferencesSection = ({
  networkPreferenceSurvey,
  networkPreferenceSurveyAnswers,
  isDirty,
  isProfileAudioEnabled = false,
  submitNetworkPreferenceSurveyResponses,
  handleSectionChange,
}: ProviderPreferencesSectionProps) => {
  const { getSectionFromMap } = useProfileContext();

  function validateForm() {
    return Object.values(networkPreferenceSurveyAnswers).every((answer) => answer !== null);
  }

  const isIncomplete = !getSectionFromMap('providerPreferences')?.isComplete;

  useEffect(() => {
    if (!isEmpty(networkPreferenceSurveyAnswers)) {
      const section = getSectionFromMap('providerPreferences');
      const isComplete = section?.isComplete || validateForm();
      handleSectionChange(validateForm(), isComplete);
    }
  }, [JSON.stringify(networkPreferenceSurveyAnswers)]);

  const handleChange = (id: number, value: NetworkPreferenceSurveyAnswer) => {
    submitNetworkPreferenceSurveyResponses({
      ...networkPreferenceSurveyAnswers,
      [id]: value,
    });
  };

  return (
    <div id="providerPreferences-section" data-testid="provider-preferences-section">
      <Question error={!validateForm() && isDirty} name="provider_preferences" isIncomplete={isIncomplete}>
        {/* The gap sits wider than the one between a prompt and its own answer, so each
        question reads as its own group rather than one run of text. */}
        {networkPreferenceSurvey.length > 0 && (
          <Stack gap={8}>
            {networkPreferenceSurvey.map((question) => (
              <ProviderPreferenceQuestion
                key={question.id}
                surveyQuestion={question}
                answer={networkPreferenceSurveyAnswers[question.id]}
                isProfileAudioEnabled={isProfileAudioEnabled}
                handleChange={handleChange}
              />
            ))}
          </Stack>
        )}
      </Question>
    </div>
  );
};
